// Two-factor setup page (for enabling/disabling 2FA)
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { ShieldCheckIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline'

export function SetupTwoFactorPage() {
  const navigate = useNavigate()
  const { user, setupTwoFactor, enableTwoFactor, disableTwoFactor } = useAuth()
  const [step, setStep] = useState<'initial' | 'setup' | 'verify' | 'disable'>('initial')
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const is2FAEnabled = user?.twoFactorEnabled ?? false

  const handleStartSetup = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await setupTwoFactor()
      setQrCode(result.qrCode)
      setSecret(result.secret)
      setStep('setup')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start setup')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      setCode(pasted.split(''))
    }
  }

  const handleVerify = async () => {
    const codeString = code.join('')
    if (codeString.length !== 6) return

    setIsLoading(true)
    setError(null)
    try {
      await enableTwoFactor(codeString)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisable = async () => {
    if (!password) return

    setIsLoading(true)
    setError(null)
    try {
      await disableTwoFactor(password)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {/* Secondary column */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Security</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage your account security settings
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-900">Two-Factor Authentication</p>
                  <p className="text-sm text-emerald-700 mt-1">
                    Add an extra layer of security to your account by requiring a code from your authenticator app.
                  </p>
                </div>
              </div>
            </div>

            {is2FAEnabled && (
              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <ShieldExclamationIcon className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">2FA is currently enabled</p>
                    <p className="text-sm text-amber-700 mt-1">
                      You'll need to enter a code from your authenticator app each time you log in.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Two-Factor Authentication</h1>
            <p className="mt-1 text-sm text-gray-500">Secure your account with 2FA</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Initial state - show enable/disable option */}
          {step === 'initial' && (
            <div className="max-w-md mx-auto">
              {is2FAEnabled ? (
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <ShieldCheckIcon className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">2FA is Enabled</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Your account is protected with two-factor authentication.
                  </p>
                  <button
                    onClick={() => setStep('disable')}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Disable 2FA
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ShieldExclamationIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">2FA is Not Enabled</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Enable two-factor authentication to add an extra layer of security.
                  </p>
                  <button
                    onClick={handleStartSetup}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? 'Setting up...' : 'Enable 2FA'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Setup step - show QR code */}
          {step === 'setup' && qrCode && secret && (
            <div className="max-w-md mx-auto text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Scan QR Code</h3>
              <p className="text-sm text-gray-600 mb-6">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>

              <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 inline-block mb-6">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
              </div>

              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2">Or enter this code manually:</p>
                <code className="text-sm font-mono bg-gray-100 px-3 py-2 rounded">{secret}</code>
              </div>

              <button
                onClick={() => setStep('verify')}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Continue
              </button>
            </div>
          )}

          {/* Verify step - enter code */}
          {step === 'verify' && (
            <div className="max-w-md mx-auto text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Verify Code</h3>
              <p className="text-sm text-gray-600 mb-6">
                Enter the 6-digit code from your authenticator app to confirm setup
              </p>

              <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    disabled={isLoading}
                    className="w-12 h-14 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-50"
                  />
                ))}
              </div>

              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setStep('setup')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleVerify}
                  disabled={isLoading || code.join('').length !== 6}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Verifying...' : 'Enable 2FA'}
                </button>
              </div>
            </div>
          )}

          {/* Disable step - enter password */}
          {step === 'disable' && (
            <div className="max-w-md mx-auto text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Disable 2FA</h3>
              <p className="text-sm text-gray-600 mb-6">
                Enter your password to confirm disabling two-factor authentication
              </p>

              <div className="mb-6">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setStep('initial')
                    setPassword('')
                    setError(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisable}
                  disabled={isLoading || !password}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              </div>
            </div>
          )}

          {/* Back link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-emerald-600"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
